import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import useEffectWhen from '~hooks/useEffectWhen';
import colorizeSvg from '../utils/colorizeSvg';
import objectHas from '../utils/objectHas';
import readSvgDimensions from '../utils/readSvgDimensions';
import sanitizeIcon from '../utils/sanitizeIcon';
import sanitizeIconVariation from '../utils/sanitizeIconVariation';
import split from '../utils/stringSplit';
import { useApi } from './Api';
import { useBackgroundColor } from './BackgroundColor';
import { useFillColor } from './FillColor';
import { useSearch } from './Search';

const IconContext = createContext<
  | {
      icons: Icon[];
      filteredIcons: Icon[];
      hasLoaded: boolean;
      stageIcon: Icon | undefined;
      openIcon: (_iconId: number) => void;
      closeIcon: () => void;
    }
  | undefined
>(undefined);

type IconProviderProps = {
  children: ReactNode;
};

function findCategoriesWithIcons(icons: Icon[], categories: string[]) {
  let availableCategories = categories.slice();
  const categoriesWithIcons: string[] = [];

  // loop through all icons
  icons.forEach(icon => {
    // loop through all available categories
    availableCategories.forEach(category => {
      // check if this category contains this icon
      if (icon.searchStack.includes(category.toLowerCase().trim())) {
        // add the found category to our return value
        categoriesWithIcons.push(category);
      }
    });

    // to prevent duplicates remove the found categories from the available categories array
    const categoriesToRemove = new Set(categoriesWithIcons);
    availableCategories = availableCategories.filter(category => !categoriesToRemove.has(category));
  });

  return categoriesWithIcons;
}

function IconProvider({ children }: IconProviderProps) {
  const { query, categories, setCategoriesWithIcons } = useSearch();
  const { selectedFillColor } = useFillColor();
  const { selectedBackgroundColor } = useBackgroundColor();
  const { fetchIconList, fetchIconSvg, fetchIconVariations } = useApi();
  const [hasLoaded, setHasLoaded] = useState(false);
  const [icons, setIcons] = useState<Icon[]>([]);
  const [filteredIcons, setFilteredIcons] = useState<Icon[]>([]);
  const [stageIconIndex, setStageIconIndex] = useState(-1);

  useEffect(() => {
    async function initialFetch() {
      const response = await fetchIconList();
      if (objectHas(response, 'items')) {
        const icons = response.items.map(sanitizeIcon);
        icons.sort((a, b) => a.title.localeCompare(b.title));
        setIcons(icons);
        setFilteredIcons(icons);

        // Filter list of available categories to match the array of icons
        setCategoriesWithIcons(findCategoriesWithIcons(icons, categories));

        setHasLoaded(true);
      }
    }
    void initialFetch();
    // eslint-disable-next-line
  }, []);

  useEffectWhen(
    () => {
      const searchTerms = split(query.toLowerCase().trim());
      const filteredIcons = icons.filter(icon =>
        // find first search term which does NOT match the icon
        searchTerms.find(term => !icon.searchStack.includes(term)) ? false : true,
      );
      setFilteredIcons(filteredIcons);
    },
    [icons, query],
    [query],
  );

  useEffectWhen(
    () => {
      async function colorizeIcon(icon: Icon) {
        if (icon.svg) {
          const colorizedSvg = await colorizeSvg(icon.svg, selectedFillColor, selectedBackgroundColor);
          setIcons(icons =>
            icons.map(item => {
              if (item.id !== icon.id) {
                return item;
              }
              return {
                ...item,
                colorizedSvg,
              };
            }),
          );
        }
      }
      if (stageIconIndex > -1) {
        void colorizeIcon(icons[stageIconIndex]);
      }
    },
    [stageIconIndex, selectedFillColor, selectedBackgroundColor, icons],
    [stageIconIndex, selectedFillColor, selectedBackgroundColor],
  );

  const openIcon = useCallback(
    (iconId: number) => {
      async function hydrateIcon(icon: Icon) {
        let { variations, svg } = icon;
        if (!svg) {
          svg = await fetchIconSvg(icon.iconUrl);
        }
        if (!variations || !Array.isArray(variations)) {
          const response = await fetchIconVariations(icon.id);
          variations = (response || []).map(sanitizeIconVariation(icon.title));
        }

        const { height, width } = await readSvgDimensions(svg);

        const colorizedSvg = await colorizeSvg(svg, selectedFillColor, selectedBackgroundColor);

        setIcons(icons =>
          icons.map(item => {
            if (item.id !== icon.id) {
              return item;
            }
            return {
              ...item,
              variations,
              svg,
              colorizedSvg,
              height,
              width,
            };
          }),
        );
      }

      const iconIndex = icons.findIndex(icon => icon.id === iconId);
      setStageIconIndex(iconIndex);
      if (iconIndex > -1) {
        void hydrateIcon(icons[iconIndex]);
      }
    },
    [icons, selectedFillColor, selectedBackgroundColor, fetchIconSvg, fetchIconVariations],
  );
  const closeIcon = useCallback(() => setStageIconIndex(-1), []);

  const value = useMemo(
    () => ({
      icons,
      filteredIcons,
      hasLoaded,
      stageIcon: stageIconIndex > -1 ? icons[stageIconIndex] : undefined,
      openIcon,
      closeIcon,
    }),
    [icons, filteredIcons, hasLoaded, openIcon, closeIcon, stageIconIndex],
  );
  return <IconContext.Provider value={value}>{children}</IconContext.Provider>;
}

function useIcon() {
  const context = useContext(IconContext);
  if (!context) {
    throw new Error(`useIcon must be used within a IconProvider`);
  }
  return context;
}

export { IconProvider, useIcon };
